/**
 * Rastreamento Google (GA4 + Google Ads) da Eco Tour Turismo.
 *
 * Regras que garantem que os dados cheguem no Google:
 *  1. O estado de consentimento PADRÃO é definido ANTES do gtag.js carregar.
 *  2. O gtag.js é carregado uma única vez para GA4 + Google Ads.
 *  3. O `config` roda com `send_page_view: false` e a visualização de página é
 *     enviada por nós em cada troca de rota (o site é SPA, então só o "config"
 *     registraria a primeira página e nunca as seguintes).
 *  4. Eventos de contato (ligação, WhatsApp, e-mail, rotas) são enviados como
 *     eventos do GA4 e, quando existe rótulo de conversão, também ao Google Ads.
 */

export const GA_MEASUREMENT_ID = "G-TWHD437QZZ";
export const COOKIE_CONSENT_KEY = "ecotour-cookie-consent-v5";

const DEBUG_FLAG_KEY = "ecotour-ga-debug";

/**
 * Analise de audiencia (estatistica) liberada por padrao para quem ainda nao
 * decidiu. E a base legal de legitimo interesse: dados anonimizados, sem uso
 * para anuncio. Para exigir o clique em "Aceitar" antes de qualquer medicao,
 * troque para false.
 */
const ANALYTICS_CONSENT_BY_DEFAULT = true;

export const GOOGLE_ADS_CONVERSION_ID = "AW-16611118395";
/** Rótulo da conversão "clique no WhatsApp" (Ads > Metas > Conversões). */
export const GOOGLE_ADS_CONVERSION_LABEL = "5qIlCKL_5b0ZELua5vA9";
/**
 * Rótulo da conversão "clique no telefone". Preencha aqui depois de criar a
 * ação de conversão de ligação no Google Ads. Enquanto estiver vazio, o clique
 * é registrado no GA4 (evento contact_phone) mas não é enviado ao Ads.
 */
export const GOOGLE_ADS_PHONE_CONVERSION_LABEL = "";

export type ConsentChoice = "granted" | "denied";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    /** Marca que o consentimento padrão já foi enviado pelo <head> do index.html. */
    __ecotourConsentDefaultSet?: boolean;
    /** Marca que o comando `js` já foi enviado (feito pelo <head>). */
    __ecotourTagJsSet?: boolean;
  }
}

let tagsInitialized = false;

function allDenied() {
  return {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
  };
}

function allGranted() {
  return {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  };
}

/**
 * Detecta o modo de depuração.
 * Abra o site com ?ga_debug=1 na URL e os eventos aparecem no
 * GA4 > Administrador > DebugView. A flag fica guardada na sessão.
 */
export function isDebugModeEnabled(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("ga_debug");
    if (flag === "1") {
      window.sessionStorage.setItem(DEBUG_FLAG_KEY, "1");
      return true;
    }
    if (flag === "0") {
      window.sessionStorage.removeItem(DEBUG_FLAG_KEY);
      return false;
    }
    return window.sessionStorage.getItem(DEBUG_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Lê a escolha já salva pelo visitante. Retorna null quando ainda não decidiu.
 */
export function getStoredConsent(): ConsentChoice | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const value = window.localStorage.getItem(COOKIE_CONSENT_KEY);
    if (value === "accepted") {
      return "granted";
    }
    if (value === "denied") {
      return "denied";
    }
    return null;
  } catch {
    return null;
  }
}

function buildDefaultConsentState() {
  const stored = getStoredConsent();
  const adsGranted = stored === "granted";
  const analyticsGranted = stored === "granted"
    || (stored === null && ANALYTICS_CONSENT_BY_DEFAULT);

  return {
    ad_storage: adsGranted ? "granted" : "denied",
    ad_user_data: adsGranted ? "granted" : "denied",
    ad_personalization: adsGranted ? "granted" : "denied",
    analytics_storage: analyticsGranted ? "granted" : "denied",
  };
}

/**
 * Inicializa GA4 + Google Ads com Consent Mode v2.
 * Idempotente: pode ser chamada quantas vezes for preciso.
 */
export function initializeGoogleTags(): void {
  if (tagsInitialized || typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  tagsInitialized = true;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args);
    };
  }

  // 1) Consentimento PADRÃO antes de qualquer tag existir.
  // Normalmente já foi enviado pelo <head> do index.html; aqui é só uma
  // rede de segurança para o caso de aquele script não ter rodado.
  if (!window.__ecotourConsentDefaultSet) {
    window.gtag("consent", "default", {
      ...buildDefaultConsentState(),
      functionality_storage: "granted",
      security_storage: "granted",
    });
    window.__ecotourConsentDefaultSet = true;
  }

  // 2) O loader (gtag.js) já está fixo no <head> do index.html — método oficial
  // do Google. Aqui só garantimos o comando `js` caso aquele script não tenha rodado.
  if (!window.__ecotourTagJsSet) {
    window.gtag("js", new Date());
    window.__ecotourTagJsSet = true;
  }

  const debugMode = isDebugModeEnabled();

  // 3) Configurações. O page_view é enviado por nós (SPA).
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
    ads_data_redaction: true,
    ...(debugMode ? { debug_mode: true } : {}),
  });

  window.gtag("config", GOOGLE_ADS_CONVERSION_ID, {
    ads_data_redaction: true,
    allow_enhanced_conversions: true,
    ...(debugMode ? { debug_mode: true } : {}),
  });
}

/**
 * Registra a escolha do visitante e atualiza o Consent Mode na hora.
 * Ao aceitar, a página atual é reenviada para que a sessão comece de fato.
 */
export function applyConsent(choice: ConsentChoice): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      COOKIE_CONSENT_KEY,
      choice === "granted" ? "accepted" : "denied",
    );
  } catch {
    /* ignore storage errors */
  }

  initializeGoogleTags();

  if (typeof window.gtag !== "function") {
    return;
  }

  if (choice === "granted") {
    window.gtag("consent", "update", allGranted());
    sendPageView(window.location.pathname + window.location.search, document.title);
    return;
  }

  window.gtag("consent", "update", allDenied());
}

/** Mantido por compatibilidade: equivale a aceitar todos os cookies. */
export function loadGoogleAnalytics(): void {
  applyConsent("granted");
}

/** Verifica se o visitante aceitou os cookies. */
export function hasAnalyticsConsent(): boolean {
  return getStoredConsent() === "granted";
}

function withDebug(params: Record<string, unknown>): Record<string, unknown> {
  return isDebugModeEnabled() ? { ...params, debug_mode: true } : params;
}

function sendPageView(path: string, title: string): void {
  if (typeof window.gtag !== "function") {
    return;
  }
  window.gtag("event", "page_view", withDebug({
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: title,
  }));
}

/** Visualização de página em cada troca de rota (obrigatório em SPA). */
export function trackPageView(path?: string, title?: string): void {
  if (typeof window === "undefined") {
    return;
  }
  initializeGoogleTags();
  sendPageView(
    path ?? `${window.location.pathname}${window.location.search}`,
    title ?? document.title,
  );
}

/** Evento genérico no GA4. */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  if (typeof window === "undefined") {
    return;
  }
  initializeGoogleTags();
  if (typeof window.gtag !== "function") {
    return;
  }
  window.gtag("event", name, withDebug(params));
  pushLog(`Evento GA4: ${name}`, Object.keys(params).length ? JSON.stringify(params) : "—");
}

/* ------------------------------------------------------------------ *
 * Painel de verificação (opcional).
 * Abra o site com ?ga_debug=1 no fim do endereço para ver, em tempo real,
 * cada evento que o site envia ao Google.
 * ------------------------------------------------------------------ */

export type TrackingLogEntry = {
  id: number;
  time: string;
  name: string;
  detail: string;
};

let logSequence = 0;
const trackingLog: TrackingLogEntry[] = [];
const logListeners = new Set<(entries: TrackingLogEntry[]) => void>();

function pushLog(name: string, detail: string): void {
  logSequence += 1;
  trackingLog.unshift({
    id: logSequence,
    time: new Date().toLocaleTimeString("pt-BR"),
    name,
    detail,
  });
  if (trackingLog.length > 10) {
    trackingLog.pop();
  }
  logListeners.forEach((listener) => listener([...trackingLog]));
}

export function subscribeTrackingLog(
  listener: (entries: TrackingLogEntry[]) => void,
): () => void {
  logListeners.add(listener);
  listener([...trackingLog]);
  return () => {
    logListeners.delete(listener);
  };
}

export function getConsentSnapshot() {
  const stored = getStoredConsent();
  const adsGranted = stored === "granted";
  const analyticsGranted = stored === "granted"
    || (stored === null && ANALYTICS_CONSENT_BY_DEFAULT);
  return { stored, adsGranted, analyticsGranted };
}

export function getConversionTarget(): string {
  return `${GOOGLE_ADS_CONVERSION_ID}/${GOOGLE_ADS_CONVERSION_LABEL}`;
}

/**
 * Evita conversão contada em dobro quando o clique passa por um botão que
 * está dentro de outro (o mesmo clique pode ser capturado duas vezes).
 */
const recentConversions: Record<string, number> = {};

function canFireConversion(key: string, windowMs = 1500): boolean {
  const now = Date.now();
  const last = recentConversions[key];
  if (typeof last === "number" && now - last < windowMs) {
    return false;
  }
  recentConversions[key] = now;
  return true;
}

/**
 * Envia a conversão ao Google Ads.
 * Usa `transport_type: "beacon"` porque o método garante que o pedido sai
 * mesmo quando o visitante troca de página/aba logo depois do clique — que é
 * exatamente o caso do botão de WhatsApp.
 */
function sendAdsConversion(label: string, params: Record<string, unknown> = {}): void {
  if (!label || typeof window.gtag !== "function") {
    return;
  }
  window.gtag("event", "conversion", withDebug({
    send_to: `${GOOGLE_ADS_CONVERSION_ID}/${label}`,
    transport_type: "beacon",
    ...params,
  }));

  const { adsGranted } = getConsentSnapshot();
  pushLog(
    "Conversão → Google Ads",
    `${GOOGLE_ADS_CONVERSION_ID}/${label}${
      adsGranted ? " · com dados de anúncio" : " · SEM dados de anúncio (banner pendente)"
    }`,
  );
}

/** Clique no botão de WhatsApp (evento + conversão Google Ads). */
export function trackWhatsAppConversion(): void {
  if (!canFireConversion("whatsapp")) {
    return;
  }
  trackEvent("contact_whatsapp", { contact_method: "whatsapp", value: 1, currency: "BRL" });
  sendAdsConversion(GOOGLE_ADS_CONVERSION_LABEL, { event_label: "whatsapp" });
}

/** Clique em um telefone (tel:) — a "tag de ligação". */
export function trackPhoneClick(phone: string, location = "site"): void {
  if (!canFireConversion("phone")) {
    return;
  }
  trackEvent("contact_phone", {
    contact_method: "phone",
    phone_number: phone,
    link_location: location,
    value: 1,
    currency: "BRL",
  });
  sendAdsConversion(GOOGLE_ADS_PHONE_CONVERSION_LABEL, { event_label: "telefone", phone_number: phone });
}

/** Clique em e-mail (mailto:). */
export function trackEmailClick(email: string, location = "site"): void {
  trackEvent("contact_email", {
    contact_method: "email",
    email_address: email,
    link_location: location,
  });
}

/** Clique em "traçar rota" / Google Maps. */
export function trackDirectionsClick(location = "site"): void {
  trackEvent("contact_directions", { contact_method: "mapa", link_location: location });
}

/** Envio de formulário. */
export function trackFormSubmit(formName: string): void {
  trackEvent("generate_lead", { contact_method: "formulario", form_name: formName, value: 1, currency: "BRL" });
}

/** Função auxiliar usada pelo rastreador global de cliques. */
export function isGoogleAdsConfigured(): boolean {
  return Boolean(GOOGLE_ADS_CONVERSION_ID && GOOGLE_ADS_CONVERSION_LABEL);
}